import {
  CAT_CRACK_MILESTONES,
  CAT_PUZZLE_LEVELS,
  type PuzzleLevelSource,
} from '@veriverse/game-logic';
import {
  CAT_SCHOLES_GAME,
  type LeaderboardPeriod,
  type LeaderboardResponse,
  type PublicUser,
  type PuzzleProgressResponse,
  type PuzzleRunResponse,
} from '@veriverse/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import crack1 from '../../../../assets-src/concept/cat-scholes/cat-scholes-cracks-1.png';
import crack2 from '../../../../assets-src/concept/cat-scholes/cat-scholes-cracks-2.png';
import crack3 from '../../../../assets-src/concept/cat-scholes/cat-scholes-cracks-3.png';
import { CAT_IDLE_FRAMES, CAT_IDLE_SHEET } from '../game/cat-scholes.js';
import { api, ApiError } from '../net/api.js';
import {
  boardCells,
  moveRun,
  puzzleCommand,
  startRun,
  undoRun,
  type PuzzleRun,
} from '../state/cat-puzzle.js';
import { useSessionStore } from '../state/session-store.js';

const FIRST_LEVEL = CAT_PUZZLE_LEVELS[0] as PuzzleLevelSource;
const CRACKS = [crack1, crack2, crack3];
const API = `/api/games/${CAT_SCHOLES_GAME}`;

type Submission =
  | { readonly state: 'idle' }
  | { readonly state: 'sending' }
  | { readonly state: 'done'; readonly result: PuzzleRunResponse; readonly crackEarned: boolean }
  | { readonly state: 'error' };

/**
 * The Cat Scholes puzzle (spec §6.14.4): push the cat (and the boxes) onto the green paw tiles in
 * as few moves as possible. The board runs here with the shared rules; a solved run is sent to
 * the server, which replays it before keeping the score. Leaderboards per level, this week and
 * all time.
 */
export function CatPuzzlePanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [progress, setProgress] = useState<PuzzleProgressResponse | undefined>();
  const [source, setSource] = useState<PuzzleLevelSource>(FIRST_LEVEL);
  const [run, setRun] = useState<PuzzleRun>(() => startRun(FIRST_LEVEL));
  const [submission, setSubmission] = useState<Submission>({ state: 'idle' });
  const [period, setPeriod] = useState<LeaderboardPeriod>('week');
  const [board, setBoard] = useState<LeaderboardResponse | undefined>();
  const [loadError, setLoadError] = useState(false);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const loadProgress = useCallback(async () => {
    try {
      const body = await api<PuzzleProgressResponse>(API);
      setProgress(body);
      setLoadError(false);
      return body;
    } catch {
      setLoadError(true);
      return undefined;
    }
  }, []);

  // Start at the first level not solved yet.
  useEffect(() => {
    void loadProgress().then((body) => {
      const next = body?.levels.find((level) => level.best === null);
      const level = next && CAT_PUZZLE_LEVELS.find((l) => l.id === next.id);
      if (level) {
        setSource(level);
        setRun(startRun(level));
      }
    });
  }, [loadProgress]);

  const loadBoard = useCallback(async (levelId: string, shown: LeaderboardPeriod) => {
    try {
      setBoard(
        await api<LeaderboardResponse>(
          `/api/games/${CAT_SCHOLES_GAME}/leaderboard?level=${levelId}&period=${shown}`,
        ),
      );
    } catch {
      setBoard(undefined);
    }
  }, []);

  useEffect(() => {
    void loadBoard(source.id, period);
  }, [source.id, period, loadBoard]);

  const choose = (level: PuzzleLevelSource) => {
    setSource(level);
    setRun(startRun(level));
    setSubmission({ state: 'idle' });
  };

  const submit = useCallback(
    async (solved: PuzzleRun) => {
      setSubmission({ state: 'sending' });
      const cracksBefore = progress?.cracks ?? 0;
      try {
        const result = await api<PuzzleRunResponse>(`${API}/levels/${solved.level.id}/runs`, {
          method: 'POST',
          body: { moves: solved.moves },
        });
        setSubmission({ state: 'done', result, crackEarned: result.cracks > cracksBefore });
        void loadProgress();
        void loadBoard(solved.level.id, period);
      } catch (error) {
        if (error instanceof ApiError && error.status === 422) setRun(startRun(source));
        setSubmission({ state: 'error' });
      }
    },
    [progress?.cracks, loadProgress, loadBoard, period, source],
  );

  const command = useCallback(
    (cmd: ReturnType<typeof puzzleCommand>) => {
      if (!cmd) return;
      if (cmd === 'restart') {
        setRun(startRun(source));
        setSubmission({ state: 'idle' });
        return;
      }
      const next = cmd === 'undo' ? undoRun(run) : moveRun(run, cmd);
      if (next === run) return;
      setRun(next);
      if (next.solved) void submit(next);
    },
    [run, source, submit],
  );

  // Keys go to the puzzle, not to the office behind it (capture phase, before Phaser sees them).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      const cmd = puzzleCommand(event);
      if (!cmd && event.key.toLowerCase() !== 'e') return;
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat || cmd !== 'restart') command(cmd);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [command, onClose]);

  const best = (id: string) => progress?.levels.find((level) => level.id === id)?.best ?? null;
  const name = (level: PuzzleLevelSource) =>
    i18n.language === 'sv' ? level.name.sv : level.name.en;
  const cracks = progress?.cracks ?? 0;
  const nextMilestone = CAT_CRACK_MILESTONES.find((m) => (progress?.solved ?? 0) < m);
  const index = CAT_PUZZLE_LEVELS.indexOf(source);
  const next = CAT_PUZZLE_LEVELS[index + 1];

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="panel cat-puzzle"
        role="dialog"
        aria-label={t('catScholes.title')}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cat-puzzle-header">
          <CatPortrait cracks={cracks} />
          <div>
            <h1>{t('catScholes.title')}</h1>
            <p className="muted">{t('catScholes.goal')}</p>
            <p className="muted">
              {t('catScholes.cracks', { count: cracks })}
              {nextMilestone !== undefined &&
                ` ${t('catScholes.nextCrack', { count: nextMilestone - (progress?.solved ?? 0) })}`}
            </p>
          </div>
        </header>

        {loadError && <p className="error">{t('catScholes.loadError')}</p>}

        <ol className="cat-levels" aria-label={t('catScholes.levels')}>
          {CAT_PUZZLE_LEVELS.map((level, i) => {
            const levelBest = best(level.id);
            return (
              <li key={level.id}>
                <button
                  type="button"
                  className={level === source ? '' : 'secondary'}
                  aria-current={level === source ? 'true' : undefined}
                  title={name(level)}
                  onClick={() => choose(level)}
                >
                  {i + 1}
                  {levelBest !== null && (
                    <span aria-label={t('catScholes.solved')}>
                      {levelBest <= level.par ? ' ★' : ' ✓'}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>

        <div className="cat-level-info">
          <strong>
            {index + 1}. {name(source)}
          </strong>
          <span className="muted">
            {t('catScholes.moves', { count: run.moves.length })} ·{' '}
            {t('catScholes.par', { count: source.par })}
            {best(source.id) !== null &&
              ` · ${t('catScholes.best', { count: best(source.id) ?? 0 })}`}
          </span>
        </div>

        <Board run={run} reducedMotion={reducedMotion} label={name(source)} />

        <div className="cat-controls">
          <button type="button" className="secondary" onClick={() => command('undo')}>
            {t('catScholes.undo')}
          </button>
          <button type="button" className="secondary" onClick={() => command('restart')}>
            {t('catScholes.restart')}
          </button>
          <span className="muted">{t('catScholes.keys')}</span>
        </div>

        <div className="cat-result" role="status" aria-live="polite">
          {submission.state === 'sending' && <p className="muted">{t('catScholes.checking')}</p>}
          {submission.state === 'error' && <p className="error">{t('catScholes.submitError')}</p>}
          {submission.state === 'done' && (
            <>
              <p>
                <strong>{t('catScholes.solvedIn', { count: submission.result.moves })}</strong>{' '}
                {submission.result.newBest
                  ? t('catScholes.newBest')
                  : t('catScholes.best', { count: submission.result.best })}
                {submission.result.moves <= source.par && ` ${t('catScholes.perfect')}`}
              </p>
              {submission.crackEarned && <p className="cat-crack">{t('catScholes.crackEarned')}</p>}
              {next && (
                <button type="button" onClick={() => choose(next)}>
                  {t('catScholes.next')}
                </button>
              )}
            </>
          )}
        </div>

        <Leaderboard
          board={board}
          period={period}
          onPeriod={setPeriod}
          optOut={progress?.leaderboardOptOut ?? false}
          onOptOut={async (value) => {
            try {
              const updated = await api<PublicUser>('/api/me/preferences', {
                method: 'PATCH',
                body: { leaderboardOptOut: value },
              });
              useSessionStore.getState().setUser(updated);
              setProgress((current) => current && { ...current, leaderboardOptOut: value });
              void loadBoard(source.id, period);
            } catch {
              /* the checkbox stays as it was */
            }
          }}
        />

        <button type="button" className="secondary" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

/** Cat Scholes with the gold cracks earned so far (idle frame 0 plus one overlay per milestone). */
function CatPortrait({ cracks }: { cracks: number }) {
  const { t } = useTranslation();
  return (
    <div className="cat-portrait" role="img" aria-label={t('catScholes.cracks', { count: cracks })}>
      <span className="cat-sprite" style={{ backgroundImage: `url(${CAT_IDLE_SHEET})` }} />
      {CRACKS.slice(0, cracks).map((src) => (
        <img key={src} src={src} alt="" />
      ))}
    </div>
  );
}

function Board({
  run,
  reducedMotion,
  label,
}: {
  run: PuzzleRun;
  reducedMotion: boolean;
  label: string;
}) {
  const { t } = useTranslation();
  const cells = boardCells(run);
  return (
    <div
      className={`cat-board${run.solved ? ' solved' : ''}`}
      role="grid"
      aria-label={label}
      style={{ gridTemplateColumns: `repeat(${run.level.width}, var(--cat-cell))` }}
    >
      {cells.flatMap((row, y) =>
        row.map((cell, x) => (
          <div
            key={`${x},${y}`}
            role="gridcell"
            className={`cat-cell ${cell.tile}`}
            aria-label={
              cell.piece
                ? t(`catScholes.pieces.${cell.piece}`)
                : cell.tile === 'target'
                  ? t('catScholes.pieces.target')
                  : undefined
            }
          >
            {cell.tile === 'target' && !cell.piece && (
              <span className="paw" aria-hidden="true">
                🐾
              </span>
            )}
            {cell.piece === 'cat' && (
              <span
                className={`cat-sprite${reducedMotion ? '' : ' idle'}`}
                style={{
                  backgroundImage: `url(${CAT_IDLE_SHEET})`,
                  ['--cat-frames' as string]: CAT_IDLE_FRAMES,
                }}
              />
            )}
            {cell.piece === 'prop' && <span className="prop" />}
            {cell.piece === 'player' && <span className="player" />}
          </div>
        )),
      )}
    </div>
  );
}

function Leaderboard({
  board,
  period,
  onPeriod,
  optOut,
  onOptOut,
}: {
  board: LeaderboardResponse | undefined;
  period: LeaderboardPeriod;
  onPeriod: (period: LeaderboardPeriod) => void;
  optOut: boolean;
  onOptOut: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="cat-leaderboard" aria-label={t('catScholes.leaderboard')}>
      <div className="row">
        <h2>{t('catScholes.leaderboard')}</h2>
        {(['week', 'all'] as const).map((p) => (
          <button
            key={p}
            type="button"
            className={p === period ? '' : 'secondary'}
            aria-pressed={p === period}
            onClick={() => onPeriod(p)}
          >
            {t(`catScholes.period.${p}`)}
          </button>
        ))}
      </div>
      {!board || board.entries.length === 0 ? (
        <p className="muted">{t('catScholes.noScores')}</p>
      ) : (
        <ol className="cat-scores">
          {board.entries.map((entry) => (
            <li key={`${entry.rank}-${entry.displayName}`} className={entry.me ? 'me' : undefined}>
              <span className="rank">{entry.rank}</span>
              <span className="name">{entry.displayName}</span>
              <span className="score">{t('catScholes.moves', { count: entry.score })}</span>
            </li>
          ))}
        </ol>
      )}
      {board?.mine && !board.entries.some((entry) => entry.me) && (
        <p className="muted">
          {t('catScholes.yourPlace', { rank: board.mine.rank, count: board.mine.score })}
        </p>
      )}
      <label className="checkbox">
        <input
          type="checkbox"
          checked={optOut}
          onChange={(event) => onOptOut(event.target.checked)}
        />
        {t('catScholes.optOut')}
      </label>
    </section>
  );
}
